import {Component, type ReactNode, type ErrorInfo} from 'react';
import {ErrorFallback} from './ErrorFallback';

interface Props {
  children: ReactNode;
  fallback?:
    | ReactNode
    | ((error: Error, errorInfo: ErrorInfo | null) => ReactNode);
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Перехватывает ошибки рендеринга в дочерних компонентах и показывает
 * fallback вместо «белого экрана». Реализован class-компонентом, т.к. React
 * пока не предоставляет hook-эквивалента `getDerivedStateFromError`.
 *
 * Адаптировано из 008-colver.front.bc-main/src/app/ErrorBoundary.tsx
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  static getDerivedStateFromError(error: Error): State {
    return {hasError: true, error, errorInfo: null};
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('React Component Error:', error);
    // eslint-disable-next-line no-console
    console.error('Component Stack:', errorInfo.componentStack);
    this.setState({error, errorInfo});
  }

  render(): ReactNode {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        if (typeof this.props.fallback === 'function') {
          return this.props.fallback(this.state.error, this.state.errorInfo);
        }
        return this.props.fallback;
      }

      return (
        <ErrorFallback
          error={this.state.error}
          componentStack={this.state.errorInfo?.componentStack}
        />
      );
    }

    return this.props.children;
  }
}
